import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, of, tap } from 'rxjs';
import { BaseEnv, Features } from '@aipb/gulliver/env/model';
import { MatFormFieldDefaultOptions } from '@angular/material/form-field';
import { MatCardConfig } from '@angular/material/card';

export interface AppConfigModel extends BaseEnv {
  ENVIRONMENT?: string;
  SENTRY_DSN?: string;
  SENTRY_VERSION_PACKAGE_NAME?: string;
  PRODUCTION?: boolean;
  GULLIVER_API_URL?: string;
  PROJECT?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AppConfigService {
  private http = inject(HttpClient);

  private _config?: Partial<AppConfigModel> = {};

  get config() {
    return this._config;
  }

  get environment(): string {
    return this._config.ENVIRONMENT || 'local';
  }

  get project(): string {
    return this._config.PROJECT || 'unknown';
  }

  get sentryDsn(): string | undefined {
    return this._config.SENTRY_DSN || undefined;
  }

  get sentryVersionPackageName(): string | undefined {
    return this._config.SENTRY_VERSION_PACKAGE_NAME || undefined;
  }

  get production(): boolean {
    return this._config.PRODUCTION || false;
  }

  get matFormFieldDefaultOptions(): MatFormFieldDefaultOptions {
    return this._config?.theme?.mat_form_field_default_options || {};
  }

  get matCardDefaultOptions(): MatCardConfig {
    return (
      this._config?.theme?.mat_card_default_options || {
        appearance: 'outlined'
      }
    );
  }

  get resolverApiUrl(): string {
    return this._config.resolverApiUrl;
  }

  get dropboxWrapperApiBaseUrl(): string {
    return this._config.dropboxWrapperApiBaseUrl;
  }

  get features(): Features {
    return this._config.features || {};
  }

  get translations() {
    return this._config.translations;
  }

  loadConfig() {
    return this.http.get<AppConfigModel>(`assets/config.json?t=${new Date().getTime()}`).pipe(
      catchError(() => of({})),
      tap((data) => {
        this._config = data;
      })
    );
  }
}
